import { Router } from '@angular/router';
import { AngularPlugin, IErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { IAnalyticsProvider } from './analytics-provider.interface';
import { routePattern } from './route-pattern';

/**
 * Azure Application Insights provider. Sits alongside {@link ClarityAnalyticsProvider} behind the
 * shared {@link IAnalyticsProvider} seam so the AnalyticsService's existing identity / view-context /
 * route-entity tagging flows to App Insights with no call-site changes.
 *
 * The Angular plugin gives us automatic PageView tracking on router navigation and — paired with the
 * `ApplicationinsightsAngularpluginErrorService` provided as the app's ErrorHandler — unhandled
 * exception capture. AJAX/dependency, performance, and browser telemetry come from the web SDK by
 * default.
 *
 * Identity and tags don't map to a native App Insights concept the way they do in Clarity, so they're
 * accumulated into `_globalProperties` and stamped onto every telemetry item via a telemetry
 * initializer. That surfaces userId / role / app / viewType / entity IDs as custom dimensions on
 * page views, dependencies, exceptions, and custom events alike — the same context Clarity gets.
 */
export class AppInsightsAnalyticsProvider implements IAnalyticsProvider {
	private _appInsights: ApplicationInsights | null = null;
	// Sticky custom dimensions applied to every telemetry item. Mutated by identifyUser/setTag and
	// read by the telemetry initializer, so context set once (or updated on navigation) rides along
	// on all subsequent telemetry without re-tagging each call.
	private readonly _globalProperties: Record<string, string> = {};

	/**
	 * @param _router Angular router the plugin listens to for PageView tracking.
	 * @param _errorService A fallback ErrorHandler the plugin forwards uncaught errors to AFTER
	 *   tracking them (e.g. to preserve console logging). It must NOT be the plugin's own registered
	 *   ErrorHandler — forwarding that service to itself is infinite recursion (see AnalyticsService).
	 */
	constructor(
		private readonly _router: Router,
		private readonly _errorService?: IErrorService
	) {}

	initialize(connectionString: string): void {
		const angularPlugin = new AngularPlugin();

		this._appInsights = new ApplicationInsights({
			config: {
				connectionString,
				extensions: [angularPlugin],
				extensionConfig: {
					[angularPlugin.identifier]: {
						router: this._router,
						// Forward uncaught exceptions to a fallback handler after tracking (console logging).
						// See the constructor doc — never pass the plugin's own error service here.
						...(this._errorService ? { errorServices: [this._errorService] } : {})
					}
				},
				// The Angular plugin already emits a PageView per navigation via `router`; leaving
				// enableAutoRouteTracking on would double-count.
				enableAutoRouteTracking: false,
				// Correlate browser AJAX calls with server-side telemetry.
				enableCorsCorrelation: true,
				disableFetchTracking: false,
				// Deliberately OFF. With it on, a telemetry send that fails (e.g. an unreachable or
				// misconfigured ingestion endpoint) surfaces as an unhandled promise rejection, which
				// this option would capture and report via trackException — issuing another send, which
				// fails, which rejects again: a self-sustaining loop that floods the endpoint and locks
				// the tab. Unhandled rejections still reach the app's ErrorHandler; we just don't let a
				// failed AI send re-enter the pipeline as new AI telemetry.
				enableUnhandledPromiseRejectionTracking: false
			}
		});

		this._appInsights.loadAppInsights();

		// Stamp accumulated identity/context onto every outgoing item as custom dimensions.
		this._appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
			item.data = item.data ?? {};
			for (const [key, value] of Object.entries(this._globalProperties)) {
				item.data[key] = value;
			}
			this._applyRoutePattern(item);
		});
	}

	identifyUser(userId: string, role: string, friendlyName?: string): void {
		// App Insights rejects auth ids containing spaces/commas/semicolons; our ids are numeric.
		this._appInsights?.setAuthenticatedUserContext(userId, undefined, true);
		this._globalProperties['userId'] = userId;
		this._globalProperties['role'] = role;
		if (friendlyName) {
			this._globalProperties['friendlyName'] = friendlyName;
		}
	}

	setTag(key: string, value: string | string[]): void {
		this._globalProperties[key] = Array.isArray(value) ? value.join(',') : value;
	}

	trackEvent(eventName: string, properties?: Record<string, unknown>): void {
		this._appInsights?.trackEvent({ name: eventName }, properties as Record<string, string | number>);
	}

	/**
	 * Names telemetry after the matched route pattern rather than the concrete URL, so pages whose URLs
	 * embed entity IDs roll up as one row instead of one row per entity.
	 *
	 * The Angular plugin sends a PageView with only a `uri`, leaving the analytics plugin to name it
	 * after `location.pathname`, and stamps `ai.operation.name` with that same pathname on everything
	 * emitted from the page. Both are rewritten here:
	 *  - PageView `name` drives the Pages view / pageViews aggregation.
	 *  - `ai.operation.name` drives the rollups for dependencies and exceptions raised on the page.
	 * The concrete URL is left intact on the PageView's `uri`, so drilling into a single entity's
	 * traffic is still possible.
	 *
	 * The pattern is read from the router here rather than from the pageId passed to identifyUser: the
	 * plugin's own NavigationEnd subscription is registered during loadAppInsights and therefore runs
	 * ahead of AnalyticsService's, so a stored pageId would still hold the previous route. Router state
	 * is already activated by the time NavigationEnd fires, making the snapshot order-independent.
	 */
	private _applyRoutePattern(item: ITelemetryItem): void {
		const pattern = routePattern(this._router);
		// Before the first navigation resolves, the snapshot has no matched children and yields '/'.
		// Leave the SDK's own naming alone rather than collapsing real telemetry onto a bogus root.
		if (pattern === '/' && this._router.url !== '/') {
			return;
		}

		item.tags = item.tags ?? [];
		item.tags['ai.operation.name'] = pattern;

		if (item.baseType === 'PageviewData' && item.baseData) {
			item.baseData['name'] = pattern;
		}
	}
}
